import React, { useCallback, useEffect, useMemo } from 'react';
import { Drawer } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { FilterWrapper } from 'shared/components/filters/filter-wrapper';
import {
  FlexWorkerSelectStateProps,
  getWorkerLabel,
} from 'hq/components/views/flexWorkers/flex-worker-select';
import { FilterWorkersSelect } from 'shared/components/filters/inputs/flex-workers/filter-workers-all-select';
import { FilterCreationDate } from 'shared/components/filters/inputs/date/filter-creation-date';
import { FilterDueDate } from 'shared/components/filters/inputs/date/filter-due-date';
import { FilterPaymentStatus } from 'shared/components/filters/inputs/payment-status/filter-payment-status';
import { FilterInvoice } from 'shared/components/filters/inputs/invoice/filter-invoice';
import { PaymentsFiltersDataProps, PaymentsFiltersFormType } from './payments-filters.types';
import {
  mapFiltersToPaymentsFilterForm,
  mapPaymentsFilterFormToFilters,
} from './map-payments-filters';

export const PaymentsFilters = ({
  appliedFilters,
  filterData,
  toggleFilters,
  handleFiltersChange,
  isOpen,
  flexWorkerOption,
  flexWorkerSearch,
  setFlexWorkerOption,
  setFlexWorkerSearch,
  selectedWorkerData,
}: PaymentsFiltersDataProps & FlexWorkerSelectStateProps) => {
  useEffect(() => {
    if (appliedFilters.workerId && selectedWorkerData) {
      const workerLabel = getWorkerLabel(selectedWorkerData);
      setFlexWorkerOption({ label: workerLabel, value: appliedFilters.workerId });
    }
  }, []);

  const appliedFilterValues = useMemo(
    () => mapFiltersToPaymentsFilterForm(appliedFilters, flexWorkerOption),
    [appliedFilters, flexWorkerOption]
  );

  const form = useForm<PaymentsFiltersFormType>({
    defaultValues: { ...appliedFilterValues },
  });

  const handleClear = useCallback(() => {
    form.reset({
      allowCreationDateFilter: false,
      creationDate: undefined,
      allowDueDateFilter: false,
      dueDate: undefined,
      allowInvoiceFilter: false,
      invoiceReference: undefined,
      allowWorkersFilter: false,
      worker: undefined,
      allowStatusFilter: false,
      status: undefined,
    });
    setFlexWorkerOption(null);
    setFlexWorkerSearch('');
  }, [appliedFilterValues, form]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      form.handleSubmit((data) => {
        const formattedData = mapPaymentsFilterFormToFilters(data);
        handleFiltersChange(formattedData);
        toggleFilters();
      })();
    },
    [form.handleSubmit, handleFiltersChange, toggleFilters, mapPaymentsFilterFormToFilters]
  );

  return (
    <Drawer anchor="right" open={isOpen} onClose={toggleFilters}>
      <FormProvider {...form}>
        <FilterWrapper
          filterName="Filter"
          onCancel={toggleFilters}
          onClear={handleClear}
          onSubmit={handleSubmit}
        >
          <FilterCreationDate />
          <FilterWorkersSelect
            workers={filterData.workers}
            flexWorkerOption={flexWorkerOption}
            flexWorkerSearch={flexWorkerSearch}
            setFlexWorkerSearch={setFlexWorkerSearch}
            setFlexWorkerOption={setFlexWorkerOption}
          />
          <FilterDueDate />
          <FilterPaymentStatus />
          <FilterInvoice />
        </FilterWrapper>
      </FormProvider>
    </Drawer>
  );
};
